/**
 * LTV drill — the per-segment LTV detail behind a click on any Customer LTV card.
 * A full-screen overlay (same chrome as DrillExplorer) showing the segment's LTV
 * markers + curve + cohort chart, with a filter rail to COMBINE first-order
 * filters (source · affiliate · discount · first product). Feeds off
 * /dashboard/ltv-drill (getLtvDrill). Opened via drill.open({ ltv: {...} }).
 */

import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useApiClient } from "../lib/api-context.js";
import { usePeriod, PERIODS, type PeriodKey } from "../lib/period.js";
import { Segmented } from "./Segmented.js";
import { CohortChart, type CohortChartRow } from "./CohortChart.js";
import { TrendChart } from "./TrendChart.js";
import { Explain } from "./cards.js";
import { useResizableWidth, useProductSkus } from "./ListControls.js";
import type { DrillRequest } from "./DrillExplorer.js";
import { PageLoadError } from "./PageLoadError.js";
import { pageLoadState } from "../lib/page-load-state.js";

interface LtvDrillData {
  curve: Array<{ day: number; avgCv: number; cohortSize: number }>;
  cohort: CohortChartRow[];
  cohortSizeMax: number;
}
type Segment = { channel?: string; affiliate?: string; discount?: string; product?: string };
const DIMS: Array<{ key: keyof Segment; label: string }> = [
  { key: "channel", label: "Source" },
  { key: "affiliate", label: "Affiliate" },
  { key: "discount", label: "Discount code" },
  { key: "product", label: "First product" },
];
const COHORT_TFS = [1, 30, 60, 90, 180];
const money = (n: number | null | undefined): string =>
  n === null || n === undefined ? "—" : new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n);

export function LtvDrill({ req, onClose, originLabel = "Dashboard" }: { req: DrillRequest; onClose: () => void; originLabel?: string }): React.ReactElement {
  const api = useApiClient();
  const globalPeriod = usePeriod();
  const skus = useProductSkus();
  const { width: railW, onGripDown } = useResizableWidth(248, "dd-drill-rail-w");
  const [seg, setSeg] = useState<Segment>(() => ({ ...req.ltv }));
  // A custom calendar range has no keyword equivalent for this drill's own window
  // picker — open on a wide 1-year view (the user can re-pick inside the drill).
  const [periodKey, setPeriodKey] = useState<PeriodKey>(globalPeriod.key === "custom" ? "365d" : globalPeriod.key);

  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const params: Record<string, string> = { period: periodKey };
  for (const d of DIMS) { const v = seg[d.key]; if (v) params[d.key] = v; }
  const ltvQ = useQuery({
    queryKey: ["ltv-drill", seg, periodKey],
    queryFn: () => api.get<LtvDrillData>("/dashboard/ltv-drill", params),
  });
  const { data, isFetching } = ltvQ;
  const requiredLoad = pageLoadState(ltvQ);

  // Filter-rail option lists (cached; only for the dropdowns).
  const productList = useQuery({ queryKey: ["drill-products"], queryFn: () => api.get<{ rows: Array<{ key: string }> }>("/dashboard/breakdown", { metric: "new_customers", dimension: "product", period: "all" }) });
  const sourceList = useQuery({ queryKey: ["drill-sources"], queryFn: () => api.get<{ bySource: Array<{ source: string }> }>("/dashboard/journey") });
  const affiliateList = useQuery({ queryKey: ["drill-affiliates"], queryFn: () => api.get<{ rows: Array<{ affiliate: string }> }>("/dashboard/ltv-by-affiliate") });
  const discountList = useQuery({ queryKey: ["drill-discounts"], queryFn: () => api.get<{ rows: Array<{ code: string }> }>("/dashboard/ltv-by-discount") });
  const optionsFor = (key: keyof Segment): string[] => {
    if (key === "product") return (productList.data?.rows ?? []).map((r) => r.key);
    if (key === "channel") return (sourceList.data?.bySource ?? []).map((r) => r.source);
    if (key === "affiliate") return (affiliateList.data?.rows ?? []).map((r) => r.affiliate);
    return (discountList.data?.rows ?? []).map((r) => r.code);
  };

  const at = (day: number): number | null => data?.curve.find((p) => p.day === day)?.avgCv ?? null;
  const activeChips = DIMS.filter((d) => seg[d.key]);
  const segLabel = activeChips.length ? activeChips.map((d) => seg[d.key]).join(" · ") : "All customers";
  const setDim = (key: keyof Segment, value: string): void => setSeg((s) => ({ ...s, [key]: value || undefined }));

  return (
    <div style={overlay} role="dialog" aria-modal="true">
      <div style={headerBar}>
        <div style={{ minWidth: 0 }}>
          <div style={crumb}>
            <button onClick={onClose} style={crumbLink}>{originLabel}</button>
            <span style={{ color: "var(--faint)" }}> › </span>
            <span style={{ color: "var(--text)", fontWeight: 600 }}>Customer LTV</span>
            {isFetching && <span style={{ color: "var(--faint)" }}> · updating…</span>}
          </div>
          <h2 style={{ margin: "2px 0 0", fontSize: 21, maxWidth: 720, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>LTV · {segLabel}</h2>
        </div>
        <button onClick={onClose} style={closeBtn} aria-label="Close">✕</button>
      </div>

      <div style={windowBar}>
        <span style={{ ...railLabel, marginBottom: 0 }}>Window</span>
        <Segmented size="sm" ariaLabel="Window" options={PERIODS.map((p) => ({ value: p.value, label: p.label }))} value={periodKey} onChange={setPeriodKey} />
      </div>

      <div style={bodyRow}>
        <div style={{ flex: 1, minWidth: 0, overflowY: "auto", padding: "18px 22px" }}>
          {requiredLoad.failed ? (
            <PageLoadError
              title="We couldn't load your customer LTV numbers"
              retrying={requiredLoad.retrying}
              onRetry={requiredLoad.retry}
            />
          ) : !data ? (
            <div style={{ color: "var(--faint)", padding: "60px 0", textAlign: "center" }}>Loading LTV…</div>
          ) : data.cohortSizeMax === 0 ? (
            <div style={{ color: "var(--faint)", padding: "60px 0", textAlign: "center" }}>No matured customers match this filter yet. Loosen a filter or widen the window.</div>
          ) : (
            <>
              <div style={tilesGrid}>
                {[{ d: 30, l: "30‑day LTV" }, { d: 90, l: "90‑day LTV" }, { d: 365, l: "365‑day LTV" }].map((m) => (
                  <div key={m.d} style={tile}>
                    <div style={tileLabel}>{m.l}</div>
                    <div style={tileVal} className="dd-num">{money(at(m.d))}</div>
                  </div>
                ))}
                <div style={tile}>
                  <div style={tileLabel}>Customers</div>
                  <div style={tileVal} className="dd-num">{data.cohortSizeMax.toLocaleString()}</div>
                </div>
              </div>

              <div style={{ ...chartCard, marginTop: 16 }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <div style={cardTitle}>How LTV builds over the year</div>
                  <Explain title="How LTV builds over the year" lines={["<b>What it is:</b> The running average value of this segment's customers as they age: 1, 30, 60, 90, 180, and 365 days after their first order.", "<b>How it's calculated:</b> For the customers in this segment, average their cumulative CM or Revenue at each day marker, matching the card's selected view.", "<b>Example:</b> A segment worth about $80 per customer on day 1 might build to $140 by day 365 as repeat orders stack up.", "<b>Note:</b> Longer markers include only customers old enough to reach them, so the far right of the curve is based on older customers."]} />
                </div>
                <div style={{ marginTop: 10 }}>
                  <TrendChart kind="line" money points={data.curve.map((p) => ({ label: p.day === 1 ? "1d" : `${p.day}d`, a: Math.round(p.avgCv) }))} />
                </div>
              </div>

              <div style={{ ...chartCard, marginTop: 16 }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <div style={cardTitle}>LTV by acquisition cohort</div>
                  <Explain title="LTV by acquisition cohort" lines={["<b>What it is:</b> Each bar is a month's worth of newly-acquired customers in this segment, showing what they're worth at each age marker.", "<b>How it's calculated:</b> Customers are grouped by the month of their first order, then their average value is measured at 30 / 90 / 180 / 365 days out.", "<b>Example:</b> Customers acquired in January reaching about $120 each by day 180.", "<b>Note:</b> Recent months can't show the longer markers yet — not enough time has passed for those customers to reach them."]} />
                </div>
                <div style={{ marginTop: 10 }}>
                  <CohortChart rows={data.cohort} timeframes={COHORT_TFS} mode="bar" />
                </div>
              </div>
            </>
          )}
        </div>

        <aside style={{ ...rail, width: railW, position: "relative" }}>
          <div onMouseDown={onGripDown} style={railGrip} title="Drag to resize" />
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
            <span style={railTitle}>Filters{activeChips.length > 0 ? ` (${activeChips.length})` : ""}</span>
            {activeChips.length > 0 && <button onClick={() => setSeg({})} style={clearAllBtn}>Clear all</button>}
          </div>
          {DIMS.map((d) => (
            <PickListGroup key={d.key} label={d.label} options={optionsFor(d.key)} value={seg[d.key]} onChange={(v) => setDim(d.key, v ?? "")} subOf={d.key === "product" ? (o) => skus[o]?.join(" · ") : undefined} />
          ))}
          <div style={{ fontSize: 11, color: "var(--faint)", lineHeight: 1.5, borderTop: "1px solid var(--hair)", paddingTop: 12 }}>
            Filters are by each customer's FIRST order, and combine — e.g. an affiliate's customers who used a code.
          </div>
        </aside>
      </div>
    </div>
  );
}

/** A labeled rail section (same look as the standard drill's filter rail). */
function RailGroup({ label, children }: { label: string; children: React.ReactNode }): React.ReactElement {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 3, borderTop: "1px solid var(--hair)", paddingTop: 12 }}>
      <div style={railLabel}>{label}</div>
      {children}
    </div>
  );
}

/** One checkable/radio row — matches the standard drill's CheckRow. `sub` shows a
 *  muted second line (e.g. the SKU codes) and lets the name wrap instead of clip. */
function CheckRow({ label, on, onClick, kind = "checkbox", sub }: { label: string; on: boolean; onClick: () => void; kind?: "checkbox" | "radio"; sub?: string }): React.ReactElement {
  return (
    <button onClick={onClick} className="dd-drill-row" style={{ ...checkRow, alignItems: sub ? "flex-start" : "center" }} aria-pressed={on} title={sub ? `${label}\n${sub}` : label}>
      {kind === "radio" ? (
        <span style={{ width: 13, height: 13, borderRadius: "50%", flexShrink: 0, marginTop: sub ? 2 : 0, border: on ? "4px solid var(--accent)" : "1.5px solid var(--border)", background: "var(--surface)" }} />
      ) : (
        <span style={{ width: 13, height: 13, borderRadius: 3.5, flexShrink: 0, marginTop: sub ? 2 : 0, border: on ? "none" : "1.5px solid var(--border)", background: on ? "var(--accent)" : "var(--surface)", color: "var(--accent-contrast)", fontSize: 10, lineHeight: "13px", textAlign: "center", fontWeight: 700 }}>{on ? "✓" : ""}</span>
      )}
      <span style={{ minWidth: 0, flex: 1 }}>
        <span style={{ display: "block", color: on ? "var(--text)" : "var(--body)", fontWeight: on ? 600 : 400, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: sub ? "normal" : "nowrap", lineHeight: 1.3 }}>{label}</span>
        {sub && <span className="dd-num" style={{ display: "block", fontSize: 10, color: "var(--faint)", marginTop: 1, lineHeight: 1.3 }}>{sub}</span>}
      </span>
    </button>
  );
}

/** Single-select, searchable list — the LTV filters pick ONE value each (one
 *  source / affiliate / discount / product), so it mirrors the standard drill's
 *  CheckListGroup look but with radios + an "All" row that clears the filter. */
function PickListGroup({ label, options, value, onChange, subOf }: { label: string; options: string[]; value: string | undefined; onChange: (v: string | undefined) => void; subOf?: (o: string) => string | undefined }): React.ReactElement {
  const [q, setQ] = useState("");
  // Search matches the option name OR its sub-line (SKU codes), so typing a SKU finds its product.
  const shown = (q ? options.filter((o) => `${o} ${subOf?.(o) ?? ""}`.toLowerCase().includes(q.toLowerCase())) : options).slice(0, 60);
  return (
    <RailGroup label={`${label}${value ? " (1)" : ""}`}>
      {options.length > 8 && (
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={`Search ${label.toLowerCase()}…`} style={railSearch} spellCheck={false} />
      )}
      <CheckRow kind="radio" label="All" on={!value} onClick={() => onChange(undefined)} />
      <div style={{ maxHeight: 180, overflowY: "auto" }}>
        {shown.map((o) => (
          <CheckRow key={o} kind="radio" label={o} sub={subOf?.(o)} on={value === o} onClick={() => onChange(value === o ? undefined : o)} />
        ))}
        {options.length > 60 && shown.length === 60 && (
          <div style={{ color: "var(--faint)", fontSize: 11, padding: "4px 7px" }}>Search to narrow…</div>
        )}
      </div>
    </RailGroup>
  );
}

const overlay: React.CSSProperties = { position: "fixed", inset: 0, background: "var(--bg)", zIndex: 100, display: "flex", flexDirection: "column", animation: "dd-fade-in .14s ease" };
const headerBar: React.CSSProperties = { display: "flex", justifyContent: "space-between", alignItems: "center", gap: 16, flexWrap: "wrap", padding: "14px 22px", borderBottom: "1px solid var(--border)", background: "var(--surface)" };
const windowBar: React.CSSProperties = { display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", padding: "10px 22px", borderBottom: "1px solid var(--border)", background: "var(--surface)" };
const crumb: React.CSSProperties = { fontSize: 12, color: "var(--body)" };
const crumbLink: React.CSSProperties = { background: "transparent", border: "none", padding: 0, color: "var(--accent)", fontSize: 12, fontWeight: 600, cursor: "pointer" };
const bodyRow: React.CSSProperties = { display: "flex", flex: 1, minHeight: 0, alignItems: "stretch" };
const chartCard: React.CSSProperties = { background: "var(--surface)", border: "1px solid var(--border)", borderRadius: "var(--r-card)", padding: "16px 18px" };
const cardTitle: React.CSSProperties = { color: "var(--text)", opacity: 0.85, fontSize: 12.5, fontWeight: 600 };
const rail: React.CSSProperties = { width: 248, flexShrink: 0, overflowY: "auto", padding: "18px 18px 24px", borderLeft: "1px solid var(--border)", background: "var(--surface)", display: "flex", flexDirection: "column", gap: 14 };
/** Drag grip on the rail's LEFT edge — widen the panel to read full product/SKU names. */
const railGrip: React.CSSProperties = { position: "absolute", left: -3, top: 0, bottom: 0, width: 7, cursor: "col-resize", zIndex: 3 };
const railTitle: React.CSSProperties = { fontSize: 12.5, fontWeight: 700, color: "var(--text)" };
const railLabel: React.CSSProperties = { fontSize: 10.5, fontWeight: 700, letterSpacing: "0.07em", textTransform: "uppercase", color: "var(--faint)", marginBottom: 4 };
const railSearch: React.CSSProperties = { width: "100%", boxSizing: "border-box", border: "1px solid var(--border)", background: "var(--surface-2)", color: "var(--text)", borderRadius: 7, padding: "6px 9px", fontSize: 12, marginBottom: 4 };
const checkRow: React.CSSProperties = { display: "flex", alignItems: "center", gap: 8, padding: "4.5px 7px", margin: "0 -7px", border: "none", background: "transparent", borderRadius: 7, cursor: "pointer", fontSize: 12.5, textAlign: "left", width: "calc(100% + 14px)" };
const clearAllBtn: React.CSSProperties = { border: "none", background: "transparent", color: "var(--accent)", fontSize: 11.5, fontWeight: 600, cursor: "pointer", padding: 0, textAlign: "left" };
const closeBtn: React.CSSProperties = { border: "1px solid var(--border)", background: "var(--surface-2)", color: "var(--body)", width: 32, height: 32, borderRadius: "var(--r-sm)", cursor: "pointer", fontSize: 13 };
const tilesGrid: React.CSSProperties = { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 12 };
const tile: React.CSSProperties = { background: "var(--surface)", border: "1px solid var(--border)", borderRadius: "var(--r-card)", padding: "12px 14px" };
const tileLabel: React.CSSProperties = { fontSize: 11.5, color: "var(--faint)", fontWeight: 600 };
const tileVal: React.CSSProperties = { fontSize: 22, color: "var(--num)", fontWeight: 700, marginTop: 3 };
